"use client"

import React, { useState } from "react"
import Link from "next/link"
import { Check, ChevronRight, Mail, MessageCircle } from "lucide-react"
import type { ContactData } from "./page"

interface Props {
  data: ContactData
  lng: string
}

// Some Windows browsers (and any OS where the default mailto handler is
// uninstalled/broken) silently swallow mailto: clicks. As a safety net we
// also copy the address to clipboard and surface a tiny "copied" hint, so
// users always come away with something useful.
async function copyToClipboardSafe(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to legacy fallback
  }
  try {
    const ta = document.createElement("textarea")
    ta.value = text
    ta.setAttribute("readonly", "")
    ta.style.position = "fixed"
    ta.style.left = "-9999px"
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

export default function ContactPageClient({ data, lng }: Props) {
  const waNumber = data.channels.whatsApp.value?.replace(/[^\d]/g, "") ?? ""
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null)
  const handleEmailClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    email: string
  ) => {
    // Don't preventDefault — let the browser also try the mailto handler.
    void copyToClipboardSafe(email).then((ok) => {
      if (ok) {
        setCopiedEmail(email)
        window.setTimeout(() => {
          setCopiedEmail((current) => (current === email ? null : current))
        }, 2000)
      }
    })
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section>
        <div className="mx-auto max-w-4xl px-5 pb-10 pt-8 md:px-10 md:pb-14 md:pt-12">
          <nav aria-label="Breadcrumb" className="mb-6 text-sm text-stone-400">
            <ol className="flex items-center gap-2">
              <li>
                <Link
                  href={`/${lng}`}
                  className="transition-colors hover:text-stone-700"
                >
                  {data.page.breadcrumbHome}
                </Link>
              </li>
              <li>/</li>
              <li className="text-stone-700">{data.page.title}</li>
            </ol>
          </nav>
          <h1 className="mb-3 text-3xl font-extrabold text-stone-900 md:text-4xl">
            {data.page.title}
          </h1>
          <p className="text-base text-stone-500 md:text-lg">
            {data.page.subtitle}
          </p>
        </div>
      </section>

      {/* Channels — 3 equal-sized cards, matches Legal pages' contact footer */}
      <section>
        <div className="mx-auto max-w-4xl px-5 pb-10 md:px-10 md:pb-14">
          <h2 className="text-center text-2xl font-bold text-stone-900 md:text-3xl">
            {data.channels.title}
          </h2>
          <p className="mt-3 text-center text-stone-500">
            {data.channels.subtitle}
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            <button
              type="button"
              onClick={() => {
                const w = window as unknown as {
                  $crisp?: { push: (args: unknown[]) => void }
                }
                if (w.$crisp) w.$crisp.push(["do", "chat:open"])
              }}
              className="flex cursor-pointer flex-col items-center rounded-2xl border border-stone-200 bg-white p-6 text-left transition-shadow hover:shadow-md"
            >
              <MessageCircle className="h-8 w-8 text-home-orange_1" />
              <p className="mt-4 text-base font-semibold text-stone-900">
                {data.channels.liveChat.title}
                {data.channels.liveChat.badge
                  ? `(${data.channels.liveChat.badge})`
                  : ""}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {data.channels.liveChat.desc}
              </p>
            </button>
            <a
              href={waNumber ? `https://wa.me/${waNumber}` : "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center rounded-2xl border border-stone-200 bg-white p-6 transition-shadow hover:shadow-md"
            >
              <svg
                className="h-8 w-8 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden
              >
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.594 5.351l-.999 3.648 3.894-.698zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01a1.094 1.094 0 00-.793.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.711.306 1.264.489 1.696.626.713.226 1.361.194 1.874.118.572-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
              </svg>
              <p className="mt-4 text-base font-semibold text-stone-900">
                {data.channels.whatsApp.title}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {data.channels.whatsApp.value}
              </p>
            </a>
            <a
              href={`mailto:${data.channels.email.value}`}
              onClick={(event) =>
                handleEmailClick(event, data.channels.email.value)
              }
              className="relative flex flex-col items-center rounded-2xl border border-stone-200 bg-white p-6 transition-shadow hover:shadow-md"
            >
              <Mail className="h-8 w-8 text-stone-600" />
              <p className="mt-4 text-base font-semibold text-stone-900">
                {data.channels.email.title}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {data.channels.email.value}
              </p>
              {copiedEmail === data.channels.email.value && (
                <span
                  role="status"
                  className="absolute right-3 top-3 rounded-full bg-stone-900 px-2 py-0.5 text-[11px] font-medium text-white"
                >
                  {lng.startsWith("zh") ? "已复制" : "Copied"}
                </span>
              )}
            </a>
          </div>
        </div>
      </section>

      {/* Topics + Checklist (side-by-side on desktop) */}
      <section>
        <div className="mx-auto max-w-4xl px-5 pb-10 md:px-10 md:pb-14">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Topics */}
            <div className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8">
              <h3 className="mb-4 text-base font-bold text-stone-900 md:text-lg">
                {data.topics.title}
              </h3>
              <ul className="space-y-2.5">
                {data.topics.items.map((t, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-sm text-stone-700 md:text-[15px]"
                  >
                    <span
                      className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-home-orange_1"
                      aria-hidden
                    />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Checklist */}
            <div className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8">
              <h3 className="mb-1 text-base font-bold text-stone-900 md:text-lg">
                {data.checklist.title}
              </h3>
              <p className="mb-4 text-xs text-stone-500 md:text-sm">
                {data.checklist.subtitle}
              </p>
              <ul className="space-y-2.5">
                {data.checklist.items.map((t, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5 text-sm text-stone-700 md:text-[15px]"
                  >
                    <span
                      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-white"
                      aria-hidden
                    >
                      <Check className="h-3 w-3 text-stone-400" aria-hidden />
                    </span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Business inquiries */}
      <section>
        <div className="mx-auto max-w-4xl px-5 pb-16 md:px-10 md:pb-20">
          <div className="flex flex-col items-start gap-4 rounded-2xl border border-stone-200 bg-stone-50/50 p-6 sm:flex-row sm:items-center sm:justify-between md:p-8">
            <div className="min-w-0">
              <p className="text-base font-bold text-stone-900 md:text-lg">
                {data.business.title}
              </p>
              <p className="mt-1 text-xs text-stone-500 md:text-sm">
                {data.business.subtitle}
              </p>
            </div>
            <a
              href={`mailto:${data.business.email}`}
              onClick={(event) => handleEmailClick(event, data.business.email)}
              className="relative inline-flex shrink-0 items-center gap-2 rounded-full bg-stone-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-stone-700"
            >
              <Mail className="h-4 w-4" aria-hidden />
              {data.business.email}
              {copiedEmail === data.business.email && (
                <span
                  role="status"
                  className="absolute -top-2 right-2 rounded-full bg-stone-700 px-2 py-0.5 text-[11px] font-medium text-white"
                >
                  {lng.startsWith("zh") ? "已复制" : "Copied"}
                </span>
              )}
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
